import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  ElectricCategoryState,
  ElectricCategoryResponse,
  ElectricCategory,
} from "./types/electric-types";
import { PaginationReturn } from "./types/types";

const initialState: ElectricCategoryState = {
  electricCategories: [],
  electricCategory: null,
  selectedElectricCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataCategory (supports FormData for image upload)
export const createElectricCategory = createAsyncThunk<
  ElectricCategoryResponse,
  FormData | Omit<ElectricCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("electricCategories/create", async (dataCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = dataCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/electric-categories`,
      dataCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return {
      message: res.data.message,
      dataCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data category" }
    );
  }
});

// READ (fetch all) ElectricCategories (with pagination)
export const fetchElectricCategories = createAsyncThunk<
  ElectricCategoryResponse & PaginationReturn,
  void | { url: string },
  { rejectValue: ApiError; state: RootState }
>("electricCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/electric-categories`;
    const res = await axios.get(apiUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.data.message,
      electricCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch electric categories" }
    );
  }
});

// READ (fetch one) DataCategory by ID
export const fetchElectricCategoryById = createAsyncThunk<
  ElectricCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("electricCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/electric-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch electric category" }
    );
  }
});

// UPDATE DataCategory (supports FormData for image upload)
export const updateElectricCategory = createAsyncThunk<
  ElectricCategory,
  { id: number; data: Partial<ElectricCategory> | FormData },
  { rejectValue: ApiError; state: RootState }
>("electricCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/electric-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update electric category" }
    );
  }
});

// DELETE DataCategory
export const deleteElectricCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("electricCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/electric-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete electric category" }
    );
  }
});

// Slice
const electricCategorySlice = createSlice({
  name: "electricCategories",
  initialState,
  reducers: {
    resetElectricCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedElectricCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createElectricCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createElectricCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.electricCategory) {
          state.electricCategories.unshift(action.payload.electricCategory);
        }
      })
      .addCase(createElectricCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create electric category";
      })

      // Fetch All
      .addCase(fetchElectricCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchElectricCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.electricCategories = action.payload.electricCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchElectricCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch electric categories";
      })

      // Fetch One
      .addCase(fetchElectricCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedElectricCategory = null;
      })
      .addCase(fetchElectricCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedElectricCategory = action.payload;
      })
      .addCase(fetchElectricCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch electric category";
      })

      // Update
      .addCase(updateElectricCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateElectricCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.electricCategories = state.electricCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        );
        if (state.selectedElectricCategory?.id === action.payload.id) {
          state.selectedElectricCategory = action.payload;
        }
      })
      .addCase(updateElectricCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update electric category";
      })

      // Delete
      .addCase(deleteElectricCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteElectricCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.electricCategories = state.electricCategories.filter(
          (category) => category.id !== action.payload
        );
      })
      .addCase(deleteElectricCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete electric category";
      });
  },
});

export const { resetElectricCategoryState } = electricCategorySlice.actions;
export default electricCategorySlice.reducer;
